"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { PRICING_CONFIG, ANNUAL_PRICING } from "@/lib/pricing-config";
import { useI18n } from "@/lib/i18n/I18nProvider";

export function PricingSection() {
  const { locale } = useI18n();
  const isEn = locale === "en";

  function fmtMonthly(usd: number, rub: number): string {
    return isEn ? `$${usd}` : `₽${rub.toLocaleString("ru-RU")}`;
  }
  function fmtAnnual(usdPm: number, rubPm: number): string {
    return isEn ? `$${usdPm}` : `₽${rubPm.toLocaleString("ru-RU")}`;
  }

  const PRICING_PLANS = [
    {
      name: "Starter",
      price: fmtMonthly(PRICING_CONFIG.starter.usd, PRICING_CONFIG.starter.rub),
      annualMonthly: fmtAnnual(ANNUAL_PRICING.starter.usdPerMonth, ANNUAL_PRICING.starter.rubPerMonth),
      period: isEn ? "per month" : "в месяц",
      description: isEn
        ? "1 property · up to 100 rooms · up to 6 revenue centers"
        : "1 объект · до 100 номеров · до 6 revenue centers",
      features: isEn
        ? ["Basic USALI (S1–S6)", "Tasks & approvals (1 level)", "KPI Dashboard", "Up to 5 users", "Email support", "Onboarding included (2 wk.)"]
        : ["USALI базовый (S1–S6)", "Задачи и согласования (1 уровень)", "KPI Dashboard", "До 5 пользователей", "Email-поддержка", "Внедрение включено (2 нед.)"],
      implementationNote: undefined as string | undefined,
      cta: isEn ? "Request demo" : "Запросить демо",
      ctaHref: "/register",
      highlighted: false,
    },
    {
      name: "Pro",
      price: fmtMonthly(PRICING_CONFIG.pro.usd, PRICING_CONFIG.pro.rub),
      annualMonthly: fmtAnnual(ANNUAL_PRICING.pro.usdPerMonth, ANNUAL_PRICING.pro.rubPerMonth),
      period: isEn ? "per month" : "в месяц",
      description: isEn
        ? "Up to 3 properties · up to 300 rooms · up to 15 revenue centers"
        : "До 3 объектов · до 300 номеров · до 15 revenue centers",
      features: isEn
        ? ["Everything in Starter +", "Full USALI (S1–S13)", "Multi-step approval chain (3 levels)", "Procurement with PO lifecycle", "Consolidated multi-property reporting", "Audit trail", "Up to 25 users", "Priority support + Slack"]
        : ["Всё из Starter +", "USALI полный (S1–S13)", "Multi-step approval chain (3 уровня)", "Procurement с PO lifecycle", "Консолидированная отчётность по сети", "Audit trail (156-ФЗ)", "До 25 пользователей", "Приоритетная поддержка + Slack"],
      implementationNote: isEn
        ? "Annual plan includes onboarding (normally $1,500)"
        : "При годовой оплате — внедрение включено (обычно ₽150 000)",
      cta: isEn ? "Request demo" : "Запросить демо",
      ctaHref: "/register",
      highlighted: true,
    },
    {
      name: "Enterprise",
      price: undefined as string | undefined,
      annualMonthly: undefined as string | undefined,
      period: undefined as string | undefined,
      description: isEn
        ? "300+ rooms · 15+ revenue centers · 3+ property portfolio"
        : "300+ номеров · 15+ revenue centers · сеть 3+ объектов",
      features: isEn
        ? ["Everything in Pro +", "1C integration (roadmap Q3)", "SSO (SAML / OIDC)", "SLA 99.95%", "Dedicated Customer Success Manager", "Custom reports & integrations", "On-premise option"]
        : ["Всё из Pro +", "1С интеграция (roadmap Q3)", "SSO (SAML / OIDC)", "SLA 99.95%", "Dedicated Customer Success Manager", "Кастомные отчёты и интеграции", "On-premise опция"],
      implementationNote: undefined as string | undefined,
      cta: isEn ? "Contact us" : "Обсудить условия",
      ctaHref: "/contact",
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            {isEn ? "Transparent pricing" : "Прозрачные цены"}
          </h2>
          <p className="text-lg text-slate-600">
            {isEn
              ? "14-day free trial. No credit card. Full access to all features."
              : "14 дней бесплатно. Без карты. Полный доступ ко всем функциям."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-6 py-4">
          {PRICING_PLANS.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-2xl transition-all duration-300 ${
                plan.highlighted
                  ? "md:scale-105 border-2 border-keiront-green bg-white shadow-lg"
                  : "border border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-keiront-green text-white px-4 py-1 rounded-full text-sm font-bold">
                    {isEn ? "POPULAR" : "ПОПУЛЯРНЫЙ"}
                  </span>
                </div>
              )}

              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-slate-600 mb-6">{plan.description}</p>

                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    {isEn ? (
                      <span className="text-2xl font-semibold text-slate-700">
                        {plan.name === "Enterprise" ? "Custom" : "Contact sales"}
                      </span>
                    ) : plan.price ? (
                      <span className="text-5xl font-bold text-slate-900">{plan.price}</span>
                    ) : (
                      <span className="text-2xl font-semibold text-slate-500">Индивидуально</span>
                    )}
                  </div>
                  {!isEn && plan.period && (
                    <p className="text-slate-600 text-sm mt-2">{plan.period}</p>
                  )}
                  {!isEn && plan.annualMonthly && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-keiro-mist px-2.5 py-0.5 text-xs font-semibold text-keiro-green-dark">
                      <span>−25% за год</span>
                      <span className="text-keiro-green">{plan.annualMonthly}/мес</span>
                    </div>
                  )}
                </div>

                <Button
                  asChild
                  size="lg"
                  variant={plan.highlighted ? "default" : "outline"}
                  className={`w-full mb-8 ${
                    plan.highlighted
                      ? "bg-keiro-green hover:bg-keiro-green-dark text-white"
                      : "border-slate-300 text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <Link href={plan.ctaHref}>{plan.cta}</Link>
                </Button>

                <div className="border-t border-slate-200 pt-6">
                  <ul className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-keiront-green flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {!isEn && plan.implementationNote && (
                    <p className="mt-4 text-xs text-keiro-green-dark bg-keiro-mist rounded-lg px-3 py-2">
                      {plan.implementationNote}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <p className="text-slate-600 mb-4">
            {isEn ? "Questions about pricing?" : "Вопросы о ценах?"}
          </p>
          <a href="#faq" className="text-keiront-green font-semibold hover:underline">
            {isEn ? "See FAQ below" : "Смотрите FAQ ниже"}
          </a>
        </div>
      </div>
    </section>
  );
}
